import { useState, useEffect, useRef } from 'react';
import { REFUND_STATUSES } from '@/common/constants/refunds';
import { findConsultantRefundsByMarketsAndStatus, updateConsultantRefund } from '@/API/repositories/consultantRefunds';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { downloadFile } from '@/common/functions/downloadFile';

export const useRefunds = () => {
    const {
		options: { marketsOptions },
	} = useCommonDataContext();
	
    const { addMessage } = useMessageQueueContext();
	const { makeRequest } = useRequestsContext();
    
    const [refunds, setRefunds] = useState([]);
	const [selectedMarkets, setSelectedMarkets] = useState(marketsOptions);
	const [selectedStatus, setSelectedStatus] = useState(REFUND_STATUSES[0]);
	const [confirmationPopup, setConfirmationPopup] = useState(false);
	const [selectedRefund, setSelectedRefund] = useState(null);
	const [loadingFiles, setLoadingFiles] = useState([]);

	const commentRef = useRef(null);
	
    const handleSearch = async () => {
		if (!selectedMarkets?.length || !selectedStatus?.value) {
			return addMessage('Please select markets and statuses', 'error');
		}
		const payload = {
			markets: selectedMarkets.map((market) => market.value),
			status: selectedStatus.value,
		};
		const response = await makeRequest(findConsultantRefundsByMarketsAndStatus.bind(null, payload));

		if (!response?.data) {
			return addMessage('Error while loading refunds', 'error');
		}

		setRefunds(response.data);
	};

	const handleDeclineRefund = async () => {
		const STATUS = 'NOT_ACCEPTED';
		
		const payload = {
			id: selectedRefund._id,
			status: STATUS,
			comment: commentRef.current?.value,
		};
		const response = await makeRequest(updateConsultantRefund.bind(null, payload));
		if (!response?.data) {
			return addMessage('Error while updating refund', 'error');
		}
		addMessage('Refund declined', 'success');
		setConfirmationPopup(false);
		setSelectedRefund(null);
		handleSearch();
	};

	const handleDownloadPdf = async (id) => {
        if (!id) {
            return addMessage('No file to download', 'error');
        }

		const file = {
			stored_document: id,
		}

        await downloadFile(file, makeRequest, setLoadingFiles);
	};

	const handleAcceptRefund = async () => {
		const STATUS = 'DONE';
		
		const payload = {
			id: selectedRefund._id,
			status: STATUS,
		};

		const response = await makeRequest(updateConsultantRefund.bind(null, payload));
		if (!response?.data) {
			return addMessage('Error while updating refund', 'error');
		}
		addMessage('Refund accepted', 'success');
		setSelectedRefund(null);
		handleSearch();
	};


    const handleCopyItem = (item) => {
        navigator.clipboard.writeText(item);
        addMessage('Copied', 'success', 500);
    };

    useEffect(() => {
		if (selectedMarkets?.length && selectedStatus?.value) {
			handleSearch();
		}
	}, []);


	return {
		refunds,
		selectedRefund,
		setSelectedRefund,
		selectedMarkets,
		setSelectedMarkets,
		selectedStatus,
		setSelectedStatus,
		handleSearch,
        handleCopyItem,
		confirmationPopup,
		setConfirmationPopup,
		commentRef,
		handleDeclineRefund,
		handleAcceptRefund,
		handleDownloadPdf,
		loadingFiles,
	};
};
